// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import { ContentCopy, OpenInNew, LinkedIn, Reply } from "@mui/icons-material";
import { AppBaseUrl, LinkedinBaseUrl } from "../../config/config";
import { AwardsPayload, CertificationsPayload } from "../../utils/types";

export default function SharableLink({
  certification,
  certificationId,
  award,
}: {
  certification?: CertificationsPayload;
  certificationId: string;
  award?: AwardsPayload;
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const shareUrl = `${AppBaseUrl}/certificate/${certificationId}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openInNewTab = () => {
    window.open(shareUrl, "_blank");
  };

  const addToLinkedin = () => {
    var url = `${LinkedinBaseUrl}?startTask=CERTIFICATION_NAME&name=${
      certification?.displayName || certification?.name
    }&organizationId=66028&certUrl=${AppBaseUrl}/certificate/${
      certification?.contact?.certificationId
    }/${certification?.id}&certId=${
      certification?.contact?.certificationId
    }&issueYear=${certification?.awardedDate?.substring(
      0,
      4
    )}&issueMonth=${certification?.awardedDate?.substring(5, 7)}`;
    window.open(url, "_blank");
  };
  
  const addAwardToLinkedIn = () => {
    var url = `${LinkedinBaseUrl}?startTask=CERTIFICATION_NAME&name=${
      award?.name
    }&organizationId=66028&certUrl=${AppBaseUrl}/certificate/${
      award?.certificationId
    }/${award?.id}&certId=${
      award?.certificationId
    }&issueYear=${award?.date?.substring(
      0,
      4
    )}&issueMonth=${award?.date?.substring(5, 7)}`;
    window.open(url, "_blank");
  }

  return (
    <>
      {(certification || award) ? (
        <Tooltip title="Share" placement="top" arrow>
          <IconButton onClick={handleOpen} sx={{ alignItems: "flex-end" }}>
          <Reply sx={{ transform: 'scaleX(-1)' }} />
          </IconButton>
        </Tooltip>
      ) : (
        <Box
          sx={{ display: "flex", alignItems: "center", textAlign: "center" }}
        >
          <Tooltip
            title="Publicly sharable link to your certifications"
            placement="top"
            arrow
          >
            <Button
              size="small"
              onClick={handleOpen}
              sx={{ px: 2 }}
              variant="outlined"
              aria-controls={open ? "account-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              endIcon={<Reply sx={{transform:'scaleX(-1)'}} />}
            >
              Share
            </Button>
          </Tooltip>
        </Box>
      )}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Share Your Certificate</DialogTitle>
        <DialogContent>
          <DialogContentText>
            By sharing this link, your full name and certification details will be publicly visible.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            value={shareUrl}
            variant="outlined"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <>
                  <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
                    <IconButton onClick={copyToClipboard}>
                      <ContentCopy color={copied ? "success" : "action"} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Open in new tab">
                    <IconButton onClick={openInNewTab}>
                      <OpenInNew />
                    </IconButton>
                  </Tooltip>
                  {certification && certification.contact.certificationId && (
                    <Tooltip title="Add to LinkedIn">
                      <IconButton onClick={addToLinkedin}>
                        <LinkedIn />
                      </IconButton>
                    </Tooltip>
                  )}
                  {award && award.certificationId && (
                    <Tooltip title="Add to LinkedIn">
                      <IconButton onClick={addAwardToLinkedIn}>
                        <LinkedIn />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
